import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Currency } from '@/lib/types';
import { toast } from 'sonner';
import { getActiveWorkspaceId } from '@/utils/workspace-helpers';

export interface CurrencyFormData {
  code: string;
  name: string;
  symbol: string;
}

export const useCurrencies = () => {
  const queryClient = useQueryClient();

  const useCurrenciesQuery = () => {
    return useQuery({
      queryKey: ['currencies', getActiveWorkspaceId()],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('currencies')
          .select('*')
          .order('name');

        if (error) {
          console.error('Error fetching currencies:', error);
          throw error;
        }

        return data as Currency[];
      },
    });
  };

  const useCurrencyQuery = (id: string) => {
    return useQuery({
      queryKey: ['currencies', getActiveWorkspaceId(), id],
      queryFn: async () => {
        if (!id) return null;

        const { data, error } = await supabase
          .from('currencies')
          .select('*')
          .eq('id', id)
          .single();

        if (error) {
          console.error('Error fetching currency:', error);
          throw error;
        }

        return data as Currency;
      },
      enabled: !!id,
    });
  };

  const useCreateCurrencyMutation = () => {
    return useMutation({
      mutationFn: async (data: CurrencyFormData) => {
        const { data: userData, error: userError } = await supabase.auth.getUser();
        if (userError) throw userError;

        const workspaceId = getActiveWorkspaceId();
        
        const { data: currencyData, error } = await supabase
          .from('currencies')
          .insert({ 
            ...data,
            user_id: userData.user.id,
            workspace_id: workspaceId
          })
          .select()
          .single();

        if (error) {
          console.error('Error creating currency:', error);
          throw error;
        }

        return currencyData as Currency;
      },
      onSuccess: () => {
        toast.success('Currency created successfully');
        queryClient.invalidateQueries({ queryKey: ['currencies'] });
      },
      onError: (error: any) => {
        console.error('Error creating currency:', error);
        toast.error(error?.message || 'Failed to create currency');
      },
    });
  };

  const useUpdateCurrencyMutation = () => {
    return useMutation({
      mutationFn: async ({
        id,
        data,
      }: {
        id: string;
        data: CurrencyFormData;
      }) => {
        const { data: currencyData, error } = await supabase
          .from('currencies')
          .update(data)
          .eq('id', id)
          .select()
          .single();

        if (error) {
          console.error('Error updating currency:', error);
          throw error;
        }

        return currencyData as Currency;
      },
      onSuccess: () => {
        toast.success('Currency updated successfully');
        queryClient.invalidateQueries({ queryKey: ['currencies'] });
      },
    });
  };

  const useDeleteCurrencyMutation = () => {
    return useMutation({
      mutationFn: async (id: string) => {
        // Check if currency is used by any contracts
        const { data: contracts } = await supabase
          .from('contracts')
          .select('id')
          .eq('currency_id', id)
          .limit(1);

        if (contracts && contracts.length > 0) {
          throw new Error('Currency is in use by contracts. Remove the currency from those contracts first.');
        }

        const { error } = await supabase
          .from('currencies')
          .delete()
          .eq('id', id);

        if (error) {
          console.error('Error deleting currency:', error);
          throw error;
        }

        return id;
      },
      onSuccess: () => {
        toast.success('Currency deleted successfully');
        queryClient.invalidateQueries({ queryKey: ['currencies'] });
      },
      onError: (error: any) => {
        console.error('Error deleting currency:', error);
        if (error?.message?.includes('in use by contracts')) {
          toast.error(error.message);
        } else if (error?.message?.includes('violates foreign key constraint')) {
          toast.error('Cannot delete this currency because it is being used by contracts, services, or exchange rates. Remove those references first.');
        } else {
          toast.error(error?.message || 'Failed to delete currency');
        }
      },
    });
  };

  return {
    useCurrenciesQuery,
    useCurrencyQuery,
    useCreateCurrencyMutation,
    useUpdateCurrencyMutation,
    useDeleteCurrencyMutation,
  };
};
